import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react-router';
import './App.css';

import MainLayout from './components/layout/MainLayout';
import EditorLayout from './components/resume/EditorLayout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ResumeProvider } from './context/ResumeContext';

// --- Page Components ---
import HomePage from '@/pages/HomePage';
import GenericPage from './pages/GenericPage';
import ContactPage from './pages/company/ContactPage';
import CareersPage from './pages/company/CareersPage';
import CareerSearchPage from './pages/company/careers/CareerSearchPage';
import JobDescriptionPage from './pages/company/careers/JobDescriptionPage';
import ApplyPage from './pages/company/careers/ApplyPage';
import ResumeDashboardPage from './pages/resume/ResumeDashboardPage';
import Contact from './pages/resume/contact';
import Experience from './pages/resume/experience';
import Education from './pages/resume/education';
import Skills from './pages/resume/skills';
import Awards from './pages/resume/awards';
import Certificates from './pages/resume/certifications';
import Summary from './pages/resume/summary';
import FinalResumePage from './pages/resume/FinalResumePage';
import Projects from './pages/resume/projects';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginPage from './pages/auth/LoginPage';
import AdminRoute from './components/auth/AdminRoute';
import AdminPage from './pages/admin/AdminPage';
import NotFoundPage from './pages/NotFoundPage';
import UserStatusPage from './pages/user/UserStatusPage';
import ChangePasswordPage from './pages/user/ChangePasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ContentManagerPage from './pages/admin/ContentManagerPage';
import ArticlePage from './pages/ArticlePage';

// --- JobBoard: feature-flagged ---
import JobBoardLegacy from './pages/job-board/JobBoard';      // .js, the old one
import JobBoardNew from './pages/job-board/JobBoard.tsx';     // .tsx, the bruh one
const USE_LEGACY_JOBBOARD = process.env.REACT_APP_LEGACY_JOBBOARD === '1';
const JobBoard = USE_LEGACY_JOBBOARD ? JobBoardLegacy : JobBoardNew;

// --- Shared QueryClient for the whole app ---
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// AuthProvider must wrap anything that calls useAuth, including App's body.
// Previously useAuth was called above the provider, returning undefined.
function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/" element={<MainLayout />}>
        <Route
          path="admin"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
        <Route path="admin/content-manager" element={
          <AdminRoute>
            <ContentManagerPage />
          </AdminRoute>
        } />
        <Route
          path="settings/change-password"
          element={
            <ProtectedRoute>
              <ChangePasswordPage />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
        {/* --- Main Site Routes --- */}
        <Route index element={<HomePage />} />

        <Route path="company" element={<Navigate to="/company/contact" replace />} />
        <Route path="company/contact" element={<ContactPage />} />
        <Route path="company/careers" element={<CareersPage />} />
        <Route path="company/careers/search" element={<CareerSearchPage />} />
        <Route path="company/careers/jobs/:jobId" element={<JobDescriptionPage />} />
        <Route path="company/careers/jobs/:jobId/apply" element={<ApplyPage />} />

        <Route path='job_board' element={<JobBoard />} />

        <Route path="login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/news/:id" element={<ArticlePage />} />
        {/* --- Resume Routes --- */}
        <Route path="resume" element={<ResumeDashboardPage />} />
        <Route path="resume/:resumeId/final" element={<FinalResumePage />} />

        <Route
          path="resume/:resumeId"
          element={
            <ProtectedRoute>
              <EditorLayout />
            </ProtectedRoute>
          }
        >
          <Route path="contact" element={<Contact />} />
          <Route path="experience" element={<Experience />} />
          <Route path="education" element={<Education />} />
          <Route path="skills" element={<Skills />} />
          <Route path="awards" element={<Awards />} />
          <Route path="certifications" element={<Certificates />} />
          <Route path="projects" element={<Projects />} />
          <Route path="summary" element={<Summary />} />
        </Route>

        <Route
          path="my-status"
          element={
            <ProtectedRoute>
              {user?.role === 'client' ? (
                <UserStatusPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )}
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <NuqsAdapter>
        <AuthProvider>
          <ResumeProvider>
            <AppRoutes />
          </ResumeProvider>
        </AuthProvider>
      </NuqsAdapter>
    </QueryClientProvider>
  );
}